import { Platform } from 'react-native';
import * as Sharing from 'expo-sharing';
import { logger } from './logger';

export interface ShareOptions {
  url?: string;
  title?: string;
  text?: string;
  mimeType?: string;
}

export interface ShareFileOptions {
  uri: string;
  filename: string;
  mimeType: string;
  dialogTitle?: string;
}

/**
 * Check if sharing is available on the current platform
 */
export const isShareAvailable = async (): Promise<boolean> => {
  if (Platform.OS === 'web') {
    return typeof navigator !== 'undefined' && !!navigator.share;
  }
  return Sharing.isAvailableAsync();
};

/**
 * Share a URL or text using the platform's native sharing
 */
export const shareUrl = async (options: ShareOptions): Promise<boolean> => {
  const { url, title, text } = options;

  if (Platform.OS === 'web') {
    // Try Web Share API first
    if (navigator.share) {
      try {
        await navigator.share({
          title,
          text,
          url,
        });
        return true;
      } catch (error: any) {
        // User cancelled or share failed
        if (error.name !== 'AbortError') {
          logger.error('Share', 'Failed to share via Web Share API', { error });
        }
        return false;
      }
    }

    // Fallback: copy to clipboard
    if (url && navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(url);
        return true;
      } catch (error) {
        logger.error('Share', 'Failed to copy to clipboard', { error });
        return false;
      }
    }

    return false;
  }

  // Mobile: use expo-sharing (for URLs, we'd typically use Share from react-native)
  // But expo-sharing is for files, so this would need react-native Share
  return false;
};

/**
 * Share a file (image, PDF, etc.) using the platform's native sharing
 */
export const shareFile = async (options: ShareFileOptions): Promise<boolean> => {
  const { uri, filename, mimeType, dialogTitle } = options;

  if (Platform.OS === 'web') {
    // Try Web Share API with file
    if (navigator.share && navigator.canShare) {
      try {
        // Convert data URI or blob URL to File
        const response = await fetch(uri);
        const blob = await response.blob();
        const file = new File([blob], filename, { type: mimeType });

        if (navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: dialogTitle || filename,
          });
          return true;
        }
      } catch (error: any) {
        if (error.name !== 'AbortError') {
          logger.error('Share', 'Web Share API file error', { error });
        }
        // Fall through to download
      }
    }

    // Fallback: trigger download
    triggerDownload(uri, filename);
    return true;
  }

  // Mobile: use expo-sharing
  try {
    const isAvailable = await Sharing.isAvailableAsync();
    if (isAvailable) {
      await Sharing.shareAsync(uri, {
        mimeType,
        dialogTitle: dialogTitle || filename,
        UTI: mimeType === 'application/pdf' ? 'com.adobe.pdf' : undefined,
      });
      return true;
    }
    return false;
  } catch (error) {
    logger.error('Share', 'Expo Sharing error', { error });
    return false;
  }
};

/**
 * Trigger a file download in the browser
 */
export const triggerDownload = (uri: string, filename: string): void => {
  if (Platform.OS !== 'web') return;

  const link = document.createElement('a');
  link.href = uri;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Convert a canvas element to a data URL
 */
export const canvasToDataUrl = (canvas: HTMLCanvasElement, mimeType = 'image/png'): string => {
  return canvas.toDataURL(mimeType);
};

/**
 * Convert an SVG element to a PNG data URL using canvas
 */
export const svgToPngDataUrl = async (
  svgElement: SVGSVGElement,
  width: number,
  height: number
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('Could not get canvas context'));
      return;
    }

    const img = new Image();
    const svgData = new XMLSerializer().serializeToString(svgElement);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    img.onload = () => {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/png'));
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load SVG'));
    };

    img.src = url;
  });
};
